/**
 * Custom error classes for PostHog integration.
 */

export interface TrackedPostHogErrorOptions {
  title: string;
  description: string;
  fingerprint?: string;
  subsystem?: string;
  cause?: Error;
}

/**
 * Error type for product-readable PostHog issue titles and descriptions.
 */
export class TrackedPostHogError extends Error {
  declare public readonly cause?: Error;
  public readonly posthogIssueTitle: string;
  public readonly posthogIssueDescription: string;
  public readonly posthogIssueFingerprint?: string;
  public readonly posthogIssueSubsystem?: string;
  public readonly posthog_issue_title: string;
  public readonly posthog_issue_description: string;
  public readonly posthog_issue_fingerprint?: string;
  public readonly posthog_issue_subsystem?: string;

  constructor(options: TrackedPostHogErrorOptions) {
    super(options.description);
    this.name = options.title;
    this.posthogIssueTitle = options.title;
    this.posthogIssueDescription = options.description;
    this.posthog_issue_title = options.title;
    this.posthog_issue_description = options.description;

    if (options.fingerprint) {
      this.posthogIssueFingerprint = options.fingerprint;
      this.posthog_issue_fingerprint = options.fingerprint;
    }
    if (options.subsystem) {
      this.posthogIssueSubsystem = options.subsystem;
      this.posthog_issue_subsystem = options.subsystem;
    }
    if (options.cause) {
      this.cause = options.cause;
    }

    Object.setPrototypeOf(this, TrackedPostHogError.prototype);
  }
}

/**
 * Base error class for PostHog-related errors.
 */
export class PostHogError extends Error {
  declare public readonly cause?: Error;

  constructor(message: string, cause?: Error) {
    super(message);
    this.name = 'PostHogError';
    if (cause !== undefined) {
      this.cause = cause;
    }
    Object.setPrototypeOf(this, PostHogError.prototype);
  }
}

/**
 * Error thrown when PostHog initialization fails.
 */
export class PostHogInitializationError extends PostHogError {
  constructor(message: string, cause?: Error) {
    super(message, cause);
    this.name = 'PostHogInitializationError';
    Object.setPrototypeOf(this, PostHogInitializationError.prototype);
  }
}

/**
 * Error thrown when PostHog initialization times out.
 */
export class PostHogTimeoutError extends PostHogError {
  constructor(message: string) {
    super(message);
    this.name = 'PostHogTimeoutError';
    Object.setPrototypeOf(this, PostHogTimeoutError.prototype);
  }
}

/**
 * Error thrown when a feature flag operation fails.
 */
export class FeatureFlagError extends PostHogError {
  declare public readonly flag: string;

  constructor(message: string, flag: string, cause?: Error) {
    super(message, cause);
    this.name = 'FeatureFlagError';
    this.flag = flag;
    Object.setPrototypeOf(this, FeatureFlagError.prototype);
  }
}
